import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ActionButton } from 'app/components/Button/ActionButton';
import ButtonDefault from 'app/components/Button/ButtonDefault';
import ModalDefault from 'app/components/Modal/ModalDefault';
import HeartBeatStatus from 'app/components/Status/HeartBeatStatus';
import { stringifyRequestQuery } from 'app/components/Table/core/helpers';
import Typography from 'app/components/Typography';
import { COLOR } from 'app/components/Typography/core/enum';
import RootContentPage from 'app/components/layout/RootContentPage';

import { ButtonEdit } from 'app/modules/inventory/items/components/ShowInfoItemCard';
import { getRacks } from 'app/modules/inventory/racks/core/requests';
import FormRackModal from 'app/modules/inventory/racks/modal/FormRack.modal';

import { usePageGoBack } from 'app/helpers/utils';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconPlus } from 'app/assets/icons/add.svg';
import { ReactComponent as EditICon } from 'app/assets/icons/edit-icon.svg';
import { ReactComponent as IconRedirect } from 'app/assets/icons/icon-redirect.svg';

import { getWard, updateWardLayout } from '../wards/core/requests';
import { Ward } from '../wards/core/type';
import { LocationMapEditor, LocationMapEditorRef } from './LocationMapEditor';
import RoomNameModal from './RoomNameModal';
import { IRect } from './core/type';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

interface IRack {
  id: string;
  type: 'custom' | 'smart';
  name: string;
}

interface IRoom {
  id: string;
}

const RACK_WIDTH = 104;
const RACK_HEIGHT = 44;
const ROOM_WIDTH = 240;
const ROOM_HEIGHT = 120;

function WardLayoutEditorPage() {
  const [roomEmpty, setRoomEmpty] = useState<IRoom>({
    id: uuidv4(),
  });

  const [ward, setWard] = useState<Ward>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [rackInWard, setRackInWard] = useState<IRack[]>([]);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapEditorRef = useRef<LocationMapEditorRef>(null);

  const [rooms, setRooms] = useState<IRect[]>([]);
  const [racks, setRacks] = useState<IRect[]>([]);
  const [roomDragging, setRoomDragging] = useState<string | null>(null);
  const [rackDragging, setRackDragging] = useState<string | null>(null);
  const [showRoomNameModal, setShowRoomNameModal] = useState(false);
  const [tempRoomPosition, setTempRoomPosition] = useState({ x: 0, y: 0 });
  const [showFormRackModal, setShowFormRackModal] = useState(false);

  const [originalRooms, setOriginalRooms] = useState<IRect[]>([]);
  const [originalRacks, setOriginalRacks] = useState<IRect[]>([]);
  const [isLayoutChanged, setIsLayoutChanged] = useState(false);
  const [isRefreshRacks, setIsRefreshRacks] = useState(false);

  const goBack = usePageGoBack(PATH.WARDS);
  const { id: wardId } = useParams();

  useEffect(() => {
    if (!wardId) {
      return;
    }
    getDataWard(Number(wardId));
  }, [wardId]);

  const getDataWard = async (idWard: number) => {
    try {
      const dataRes = await getWard(idWard);

      if (dataRes) {
        const warehouse = dataRes.warehouse;
        setWard(dataRes);

        if (!dataRes.chore) {
          return;
        }

        const stageConfig = JSON.parse(JSON.parse(dataRes.chore || '{}'));

        const extractedRooms: any[] = [];
        const extractedRacks: any[] = [];

        stageConfig?.children?.[0]?.children?.forEach((child) => {
          if (child.className === 'Group') {
            if (child.attrs.id?.startsWith('room-')) {
              extractedRooms.push({
                id: child.attrs.id,
                x: child.attrs.x,
                y: child.attrs.y,
                width: child.attrs.width,
                height: child.attrs.height,
                scaleX: child.attrs.scaleX || 1,
                scaleY: child.attrs.scaleY || 1,
                rotation: child.attrs.rotation,
                skewX: child.attrs.skewX,
                skewY: child.attrs.skewY,
                offsetX: child.attrs.offsetX,
                offsetY: child.attrs.offsetY,
                rectAttrs: child.children?.find((c) => c.className === 'Rect')?.attrs,
                textAttrs: child.children?.find((c) => c.className === 'Text')?.attrs,
              });
            } else if (child.attrs.id?.startsWith('rack-')) {
              const rackId = child.attrs.id.replace('rack-', '');
              const rack = warehouse?.find((w) => w.id === Number(rackId));
              let textAttrs = child.children?.find((c) => c.className === 'Text')?.attrs;
              if (rack) {
                textAttrs = {
                  ...textAttrs,
                  text: rack.name,
                };
              }
              extractedRacks.push({
                id: child.attrs.id,
                x: child.attrs.x,
                y: child.attrs.y,
                width: child.attrs.width,
                height: child.attrs.height,
                rotation: child.attrs.rotation,
                skewX: child.attrs.skewX,
                skewY: child.attrs.skewY,
                offsetX: child.attrs.offsetX,
                offsetY: child.attrs.offsetY,
                scaleX: child.attrs.scaleX || 1,
                scaleY: child.attrs.scaleY || 1,
                type: child.attrs.type || '',
                fill: child.children?.find((c) => c.className === 'Rect')?.attrs.fill || '',
                rectAttrs: child.children?.find((c) => c.className === 'Rect')?.attrs,
                textAttrs: textAttrs,
              });
            }
          }
        });

        setRooms(extractedRooms);
        setRacks(extractedRacks);

        // Store original state for comparison
        setOriginalRooms(JSON.parse(JSON.stringify(extractedRooms)));
        setOriginalRacks(JSON.parse(JSON.stringify(extractedRacks)));
        setIsLayoutChanged(false);
      }
    } catch (error) {
      goBack();
    }
  };

  const refreshRacksInWard = async () => {
    if (!ward || !wardId) return;

    const dataResRacks = await getRacks(
      `${stringifyRequestQuery({
        page: 1,
        limit: 99999999,
      })}&wardId=${wardId}`,
    );

    // Filter out racks that are already in the racks state
    const existingRackIds = racks.map((rack) => rack.id.replace('rack-', ''));

    const formattedRacks = dataResRacks.data?.items
      .filter((rack) => !existingRackIds.includes(rack.id?.toString() || ''))
      .map((rack) => ({
        id: rack.id,
        type: rack.isCustom ? 'custom' : 'smart',
        name: rack.name,
      }))
      .sort((a, b) => {
        return Number(a.id) - Number(b.id);
      }) as IRack[];

    setRackInWard(formattedRacks || []);
  };

  useEffect(() => {
    if (!ward) {
      return;
    }

    refreshRacksInWard();
  }, [isRefreshRacks, ward]);

  useEffect(() => {
    const updateDimensions = () => {
      if (mapContainerRef.current) {
        setMapDimensions({
          width: mapContainerRef.current.offsetWidth,
          height: mapContainerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();

    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, [isEditMode]);

  useEffect(() => {
    // Deep comparison function
    const isEqual = (arr1: any[], arr2: any[]) => {
      if (arr1.length !== arr2.length) return false;

      // Sort arrays by id to ensure consistent comparison
      const sortedArr1 = [...arr1].sort((a, b) => a.id.localeCompare(b.id));
      const sortedArr2 = [...arr2].sort((a, b) => a.id.localeCompare(b.id));

      return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
    };

    const roomsChanged = !isEqual(rooms, originalRooms);
    const racksChanged = !isEqual(racks, originalRacks);

    setIsLayoutChanged(roomsChanged || racksChanged);
  }, [rooms, racks, originalRooms, originalRacks]);

  const handleStop = (id, type, e, data) => {
    let x = data.x;
    let y = data.y;

    if (e.clientX && e.clientY && mapContainerRef.current) {
      const containerRect = mapContainerRef.current.getBoundingClientRect();

      const isInsideContainer =
        e.clientX >= containerRect.left &&
        e.clientX <= containerRect.right &&
        e.clientY >= containerRect.top &&
        e.clientY <= containerRect.bottom;

      if (!isInsideContainer) {
        setRoomDragging(null);
        setRackDragging(null);
        return;
      }

      const elementWidth = type === 'room' ? ROOM_WIDTH : RACK_WIDTH;
      const elementHeight = type === 'room' ? ROOM_HEIGHT : RACK_HEIGHT;

      x = e.clientX - containerRect.left - elementWidth / 2;
      y = e.clientY - containerRect.top - elementHeight / 2;

      x = Math.max(0, Math.min(x, containerRect.width - elementWidth));
      y = Math.max(0, Math.min(y, containerRect.height - elementHeight));
    } else {
      return;
    }

    if (type === 'room') {
      setTempRoomPosition({ x, y });
      setShowRoomNameModal(true);
    } else if (type === 'rack') {
      const rackDraggingCurrent = rackInWard.find((rack) => rack.id === id);

      if (!rackDraggingCurrent) {
        return;
      }

      const newRack = {
        ...rackDraggingCurrent,
        x: x,
        y: y,
        width: RACK_WIDTH,
        height: RACK_HEIGHT,
        scaleX: 1,
        scaleY: 1,
        id: `rack-${rackDraggingCurrent.id}`,
        name: rackDraggingCurrent.name,
        rectAttrs: {
          width: RACK_WIDTH,
          height: RACK_HEIGHT,
          // fill: rackDraggingCurrent.type === 'smart' ? '#0080F0' : COLOR.WARNING,
          // stroke: rackDraggingCurrent.type === 'smart' ? '#006FDE' : '#D39400',
          fill: COLOR.WARNING,
          stroke: COLOR.WARNING,
          strokeWidth: 2,
          cornerRadius: 2,
        },
        textAttrs: {
          x: 10,
          y: RACK_HEIGHT - 15,
          text: rackDraggingCurrent.name,
          fontStyle: 'bold',
          fill: '#FFFFFF',
        },
      };
      setRacks([...racks, newRack]);

      setRackInWard(rackInWard.filter((rack) => rack.id !== id));
    }
    setRoomDragging(null);
    setRackDragging(null);
  };

  const handleDragStart = (id, type, e) => {
    if (type === 'room') {
      setRoomDragging(id);
    } else if (type === 'rack') {
      setRackDragging(id);
    }
  };

  const handleSaveButtonClick = async () => {
    if (mapEditorRef.current) {
      const stageData = mapEditorRef.current.getStageData();

      const request = {
        chore: JSON.stringify(stageData),
      };

      try {
        await updateWardLayout(wardId, request);

        setIsLayoutChanged(false);
        setIsEditMode(false);
        toast.success('Save layout successfully');
      } catch (error) {
        toast.error('Save layout failed');
      }
    }
  };

  const handleSaveRoomName = (roomName: string) => {
    const newRoom = {
      id: `room-${uuidv4()}`,
      x: tempRoomPosition.x,
      y: tempRoomPosition.y,
      width: ROOM_WIDTH,
      height: ROOM_HEIGHT,
      scaleX: 1,
      scaleY: 1,
      name: roomName,
      rectAttrs: {
        width: ROOM_WIDTH,
        height: ROOM_HEIGHT,
        fill: '#F7F9FC',
        strokeWidth: 3,
        stroke: '#7E8299',
      },
      textAttrs: {
        x: 20,
        y: 20,
        text: roomName,
        fontSize: 16,
        fontStyle: 'bold',
        fill: '#181C32',
      },
    };

    setRooms([...rooms, newRoom]);

    setRoomEmpty({
      id: uuidv4(),
    });
  };

  const handleAddRack = () => {
    setShowFormRackModal(true);
  };

  const handleClickCloseFormRackModal = () => {
    setShowFormRackModal(false);
    refreshRacksInWard();
  };

  const handleResetLayout = () => {
    setIsEditMode(false);
    setRooms(originalRooms);
    setRacks(originalRacks);
    setIsLayoutChanged(false);
  };

  const handleDeleteRack = (id: string) => {
    setRacks(racks.filter((rack) => rack.id !== id));
    setIsLayoutChanged(true);
    setIsRefreshRacks(!isRefreshRacks);
  };

  const handleToggleEditMode = () => setIsEditMode(!isEditMode);

  const hanldeUpdateRackPosition = (id: string, x: number, y: number) => {
    setRacks((prevRacks) => prevRacks.map((rack) => (rack.id === id ? { ...rack, x, y } : rack)));
  };

  const handleUpdateRoomPosition = (id: string, x: number, y: number) => {
    setRooms((prevRooms) => prevRooms.map((room) => (room.id === id ? { ...room, x, y } : room)));
  };

  const handleOutOfBoundsDrag = (isOverLap = false) => {
    if (isOverLap) {
      toast.warning('Item cannot overlap with other racks or rooms');
      return;
    }

    toast.warning('Item cannot be placed outside the map boundaries');
  };

  const handleDragEndRefresh = () => setIsRefreshRacks(!isRefreshRacks);

  return (
    <>
      <div
        style={{ height: '100vh', width: '100%', gap: 24, display: 'flex', position: 'relative' }}
      >
        <div
          className={clsx({
            'd-none': !isEditMode,
            'animate__animated animate__fadeInLeft': isEditMode,
          })}
          style={{
            width: '20%',
            height: '100vh',
            marginLeft: '-40px',
            borderRight: '1px solid var(--neutral-4)',
            backgroundColor: '#FFFFFF',
            paddingTop: 32,
          }}
        >
          <div className="d-flex flex-column" style={{ padding: '0px 24px 32px', gap: 8 }}>
            <Typography variant="title-2" style={{ color: 'var(--neutral-10)' }} fontWeight={700}>
              Add room
            </Typography>

            <div
              style={{
                width: '100%',
                height: ROOM_HEIGHT,
                backgroundColor: 'var(--neutral-1)',
                border: '3px solid var(--neutral-7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                opacity: roomDragging === roomEmpty.id ? 0.5 : 1,
              }}
              draggable
              onDragStart={(e) => handleDragStart(roomEmpty.id, 'room', e)}
              onDragEnd={(e) => {
                const data = { x: e.clientX, y: e.clientY };
                handleStop(roomEmpty.id, 'room', e, data);
              }}
            >
              <Typography variant="body-5" fontSize={12} style={{ color: 'var(--neutral-6)' }}>
                Drag to canvas to add a room
              </Typography>
            </div>
          </div>

          <div className="d-flex flex-column" style={{ gap: 8, padding: '0px 23px 32px 24px' }}>
            <div className="d-flex align-items-center justify-content-between">
              <Typography
                variant="title-2"
                style={{ lineHeight: '26px', color: 'var(--neutral-10)' }}
                fontWeight={700}
              >
                Available racks
              </Typography>
              <div
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: 'var(--neutral-2)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={handleAddRack}
              >
                <IconPlus />
              </div>
            </div>

            <div className="d-flex" style={{ gap: 24, paddingTop: '4px' }}>
              <div className="d-flex" style={{ gap: 8 }}>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      // backgroundColor: '#0080F0',
                      backgroundColor: COLOR.WARNING,
                      borderRadius: 4,
                    }}
                  ></div>
                  <Typography variant="body-5" style={{ color: 'var(--neutral-10)' }}>
                    Smart rack
                  </Typography>
                </div>
              </div>

              {/* <div className="d-flex" style={{ gap: 8 }}>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <div
                    style={{ width: 12, height: 12, backgroundColor: COLOR.WARNING, borderRadius: 4 }}
                  ></div>
                  <Typography variant="body-5" style={{ color: 'var(--neutral-10)' }}>
                    Custom rack
                  </Typography>
                </div>
              </div> */}
            </div>

            <div className="d-flex" style={{ flexWrap: 'wrap' }}>
              {rackInWard.map((rack) => (
                <div key={rack.id} style={{ padding: 8 }}>
                  <div
                    className="d-flex"
                    style={{
                      justifyContent: 'flex-start',
                      alignItems: 'flex-end',
                      width: RACK_WIDTH,
                      height: RACK_HEIGHT,
                      // backgroundColor: rack.type === 'smart' ? '#0080F0' : COLOR.WARNING,
                      // border: rack.type === 'smart' ? '2px solid #006FDE' : '2px solid #D39400',
                      backgroundColor: COLOR.WARNING,
                      border: `2px solid ${COLOR.warning_active_2}`,
                      borderRadius: 4,
                      paddingLeft: '4px',
                      opacity: rackDragging === rack.id ? 0.5 : 1,
                      position: 'relative',
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(rack.id, 'rack', e)}
                    onDragEnd={(e) => {
                      const data = { x: e.clientX, y: e.clientY };
                      handleStop(rack.id, 'rack', e, data);
                    }}
                  >
                    <Typography className="w-100 text-ellipsis text-white" variant="title-5">
                      {rack.name}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="h-100" style={{ width: isEditMode ? '80%' : '100%' }}>
          <RootContentPage
            rootBody={{
              padding: 0,
              border: 'none',
              height: 'calc(100% - 67px)',
            }}
            minHeightHeader={54}
            title=""
            header={
              ward?.name && (
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex  align-items-center">
                    <div
                      className="d-flex align-items-center"
                      // style={{
                      //   borderRight: `1px solid var(--neutral-4)`,
                      // }}
                    >
                      <IconRedirect
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={goBack}
                      />
                      <Typography
                        variant="heading-6"
                        style={{
                          marginRight: 24,
                        }}
                      >
                        {ward.name}
                      </Typography>
                    </div>
                    {/* <div
                      className="d-flex flex-column align-items-start justify-content-between"
                      style={{ marginLeft: 24 }}
                    >
                      <Typography variant="title-5" style={{ marginBottom: 4 }}>
                        {'Raspberry Pi connection'}
                      </Typography>
                      <HeartBeatStatus pageType="ward" targetId={Number(ward.id)} />
                    </div> */}
                  </div>
                  <div className="d-flex gap-4">
                    {!isEditMode ? (
                      <ButtonEdit
                        className="btn d-flex justify-content-center align-items-center gap-2"
                        onClick={handleToggleEditMode}
                      >
                        <EditICon />
                        <Typography
                          variant="title-4"
                          className="animate__animated animate__fadeIn"
                          color="primary"
                        >
                          Edit mode
                        </Typography>
                      </ButtonEdit>
                    ) : (
                      <div className="animate__animated animate__fadeIn d-flex gap-4">
                        <ActionButton
                          notMarginTop
                          isShowButtonReset={false}
                          onSubmit={handleSaveButtonClick}
                          labelButtonSubmit="Save layout"
                          disableButton={!isLayoutChanged}
                        />
                        <ButtonDefault theme="btn-light" onClick={handleResetLayout}>
                          Cancel
                        </ButtonDefault>
                      </div>
                    )}
                  </div>
                </div>
              )
            }
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                padding: 48,
              }}
            >
              <div
                ref={mapContainerRef}
                style={{
                  width: '100%',
                  height: '100%',
                  border: `3px solid var(--neutral-7)`,
                  backgroundColor: 'var(--neutral-1)',
                }}
              >
                <LocationMapEditor
                  handleDeleteRack={handleDeleteRack}
                  setIsLayoutChanged={setIsLayoutChanged}
                  ref={mapEditorRef}
                  width={mapDimensions.width}
                  height={mapDimensions.height}
                  rooms={rooms}
                  racks={racks}
                  isEditMode={isEditMode}
                  onOutOfBoundsDrag={handleOutOfBoundsDrag}
                  onUpdateRackPosition={hanldeUpdateRackPosition}
                  onUpdateRoomPosition={handleUpdateRoomPosition}
                  onDragEndRefresh={handleDragEndRefresh}
                />
              </div>
            </div>
          </RootContentPage>
        </div>
      </div>

      {showRoomNameModal && (
        <RoomNameModal
          onClickCloseModal={() => setShowRoomNameModal(false)}
          onSaveRoomName={handleSaveRoomName}
        />
      )}

      {showFormRackModal && (
        <ModalDefault
          title="Add rack"
          onClickCloseModal={handleClickCloseFormRackModal}
          closeOnBackdrop={false}
          styleModalDialog={{ width: 500 }}
        >
          <FormRackModal
            onClickCloseModal={handleClickCloseFormRackModal}
            wards={ward ? [ward] : []}
            isAddRackForWard
          />
        </ModalDefault>
      )}
    </>
  );
}

export default WardLayoutEditorPage;
